import { useState, useEffect } from 'react';
import PT from 'prop-types';
import IPT from 'react-immutable-proptypes';
import LoadingIndicator from 'edrolo/domains/app/components/LoadingIndicator';
import SuccessMeasureOption from './SuccessMeasureOption';
import { Button } from 'edrolo-style-guide';
import { SUCCESS_RESPONSES, ANALYTICS_CONSTANTS } from './constants';
import DislikeThumbSvg from './DislikeThumbSvg';
import PartlyThumbSvg from './PartlyThumbSvg';
import LikeThumbSvg from './LikeThumbSvg';
import { useProfile } from 'edrolo/domains/profile';
import styles from './SuccessMeasure.scss';
import { useSuccessMeasureResponses } from 'edrolo/domains/lessons-chunkview/hooks';
import { useInjectable } from 'core/hooks';
import { chunkPropType } from '../../../lessons/propTypes';
import { useAnalytics } from 'edrolo/analytics';

const SuccessMeasure = ({
  chunk,
  lessonId,
  nextChunkUrl,
  successMeasureChunks,
}) => {
  const { profile } = useProfile(!!'immutable');
  const { track } = useAnalytics({
    eventAttributes: {
      page_name: ANALYTICS_CONSTANTS.PAGE_NAME, // TODO: this doesn't seem right but persisting..
    },
  });
  const profileId = profile.get('id') || 1; // TODO: profileId isn't needed for anything, no need for reducer to use it.
  const successMeasureId = chunk.getIn(['success_measure', 'id']);
  const useSuccessMeasureResponsesInjectable = useInjectable(
    'useSuccessMeasureResponsesInjectable',
    useSuccessMeasureResponses
  );

  const {
    data: successMeasureResponses,
    loading,
    save: recordSuccessMeasureResponse,
  } = useSuccessMeasureResponsesInjectable(successMeasureId, lessonId);

  const existingResponse = successMeasureResponses
    .filter((res) => res.get('success_measure_id') === successMeasureId)
    .last();

  const [responseValue, setResponseValue] = useState(
    existingResponse?.get('value') || null
  );
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    // resets selected responseValue when you navigate to the new
    // success measure chunk (aka when the succesMeasureID changes)
    const existingResponse = successMeasureResponses
      .filter((res) => res.get('success_measure_id') === successMeasureId)
      .last();
    setResponseValue(existingResponse?.get('value') || null);
  }, [
    existingResponse,
    successMeasureId,
    setResponseValue,
    successMeasureResponses,
  ]);

  const handleSave = () => {
    recordSuccessMeasureResponse(
      lessonId,
      profileId,
      successMeasureId,
      responseValue,
      nextChunkUrl
    );

    track('success measure saved', {
      rating: responseValue,
    });
    setSaved(true);
  };

  if (!lessonId) {
    return <LoadingIndicator loadingText="Loading Lesson" />;
  }

  return (
    <SuccessMeasurePres
      chunk={chunk}
      successMeasureChunks={successMeasureChunks}
      response={{ responseValue, setResponseValue }}
      savedState={{ saved, setSaved }}
      handleSave={handleSave}
      disabled={loading}
    />
  );
};

export const SuccessMeasurePres = ({
  chunk,
  response,
  handleSave,
  disabled,
  savedState,
}) => {
  const { responseValue, setResponseValue } = response;
  const { saved, setSaved } = savedState;
  const description = chunk.getIn(['success_measure', 'description']);
  const colorCode = chunk.getIn(['success_measure', 'color_code']);

  return (
    <div className={styles.container}>
      <h3 style={{ color: `${colorCode}` }} data-test="success-measure-h3">
        Success Measure
      </h3>
      <p>{description}</p>

      <div className={styles.options} data-test="success-measure-option">
        <SuccessMeasureOption
          label={SUCCESS_RESPONSES.NOT_YET.label}
          IconComponent={DislikeThumbSvg}
          value={SUCCESS_RESPONSES.NOT_YET.value}
          selectedValue={responseValue}
          setValue={setResponseValue}
          setSaved={setSaved}
        />

        <SuccessMeasureOption
          label={SUCCESS_RESPONSES.PARTLY.label}
          IconComponent={PartlyThumbSvg}
          value={SUCCESS_RESPONSES.PARTLY.value}
          selectedValue={responseValue}
          setValue={setResponseValue}
          setSaved={setSaved}
        />

        <SuccessMeasureOption
          label={SUCCESS_RESPONSES.YES.label}
          IconComponent={LikeThumbSvg}
          value={SUCCESS_RESPONSES.YES.value}
          selectedValue={responseValue}
          setValue={setResponseValue}
          setSaved={setSaved}
        />
      </div>

      <div className={styles.saveButton} data-test="success-measure-link">
        <Button
          small
          disabled={disabled || !responseValue}
          onClick={handleSave}
        >
          {saved ? 'Submitted!' : 'Submit response'}
        </Button>
      </div>
    </div>
  );
};

export default SuccessMeasure;

SuccessMeasure.propTypes = {
  chunk: chunkPropType,
  lessonId: PT.number,
  nextChunkUrl: PT.string,
  successMeasureChunks: IPT.listOf(chunkPropType),
};

SuccessMeasurePres.propTypes = {
  chunk: chunkPropType,
  response: PT.shape({
    responseValue: PT.string,
    setResponseValue: PT.func,
  }),
  savedState: PT.shape({
    saved: PT.bool,
    setSaved: PT.func,
  }),
  handleSave: PT.func,
  disabled: PT.bool,
};
